import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useThemeStore } from '../state/theme';
import { useAuthStore } from '../state/auth';
import { useTenantStore } from '../state/tenant';
import { useTourStore } from '../state/tour';
import { TESORERIA_HOME_TOUR_ID } from '../lib/tours/tesoreriaHomeTour';
import { MEMBRESIAS_HOME_TOUR_ID } from '../lib/tours/membresiasHomeTour';
import { LOGISTICA_HOME_TOUR_ID } from '../lib/tours/logisticaHomeTour';
import { logout } from './AuthBoundary';
import { api } from '../lib/api';
import { auth } from '../lib/firebase';
import { BrandLogo } from './ui/BrandLogo';
import { PersonAvatar } from './ui/PersonAvatar';
import {
  QuickNavigationSearch,
  type QuickNavigationItem,
} from './navigation/QuickNavigationSearch';
import { quickActionDefinitions } from './navigation/quickActions';
import { CaretDown, Question } from '@phosphor-icons/react';
import { KyraIcon, kyraIcons } from './ui/KyraIcon';
import type { IconLike } from './ui/KyraIcon';
import { ModuleTile } from './ui/ModuleTile';

type ModuleKey =
  'tesoreria' | 'membresias' | 'logistica' | 'configuracion' | 'sitio_publico' | 'superadmin';
type NavSectionKey =
  | 'daily'
  | 'analysis'
  | 'membership_ops'
  | 'log_ops'
  | 'log_inventory'
  | 'log_analysis'
  | 'log_admin'
  | 'config_general'
  | 'public_site'
  | 'super_platform'
  | 'super_commercial';

type NavItem = {
  to: string;
  label: string;
  icon: IconLike;
  section: NavSectionKey;
  module: ModuleKey;
  adminOnly?: boolean;
  ownerOnly?: boolean;
  publicSiteManageOnly?: boolean;
  requiredScope?: string;
  requiredFeatures?: string[];
  requiredCapabilitiesAny?: string[];
};

const moduleMeta: Record<ModuleKey, { label: string; defaultRoute: string; accent: string }> = {
  tesoreria: { label: 'Tesorería', defaultRoute: '/tesoreria', accent: 'var(--module-tesoreria)' },
  membresias: {
    label: 'Membresías',
    defaultRoute: '/membresias',
    accent: 'var(--module-membresias)',
  },
  logistica: { label: 'Logística', defaultRoute: '/logistica', accent: 'var(--module-logistica)' },
  configuracion: {
    label: 'Configuración',
    defaultRoute: '/configuracion',
    accent: 'var(--module-configuracion)',
  },
  sitio_publico: {
    label: 'Sitio público',
    defaultRoute: '/public',
    accent: 'var(--module-sitio-publico)',
  },
  superadmin: {
    label: 'Superadmin',
    defaultRoute: '/superadmin',
    accent: 'var(--module-superadmin)',
  },
};

const moduleTourIds: Partial<Record<ModuleKey, string>> = {
  tesoreria: TESORERIA_HOME_TOUR_ID,
  membresias: MEMBRESIAS_HOME_TOUR_ID,
  logistica: LOGISTICA_HOME_TOUR_ID,
};

const LAST_MODULE_SESSION_KEY = 'kyra:last-module';

const isModuleKey = (value: unknown): value is ModuleKey =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(moduleMeta, value);

const readLastModule = (): ModuleKey => {
  if (typeof window === 'undefined') return 'tesoreria';
  try {
    const storedModule = window.sessionStorage.getItem(LAST_MODULE_SESSION_KEY);
    return isModuleKey(storedModule) ? storedModule : 'tesoreria';
  } catch {
    return 'tesoreria';
  }
};

const persistLastModule = (module: ModuleKey) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LAST_MODULE_SESSION_KEY, module);
  } catch {
    // La navegación continúa aunque el navegador bloquee el almacenamiento de sesión.
  }
};

const navItems: NavItem[] = [
  {
    to: '/public',
    label: 'Landing',
    icon: kyraIcons.landing,
    section: 'public_site',
    module: 'sitio_publico',
  },
  {
    to: '/public/blog',
    label: 'Blog',
    icon: kyraIcons.blog,
    section: 'public_site',
    module: 'sitio_publico',
  },
  {
    to: '/public/editores',
    label: 'Editores',
    icon: kyraIcons.edit,
    section: 'public_site',
    module: 'sitio_publico',
    publicSiteManageOnly: true,
  },
  {
    to: '/superadmin',
    label: 'Resumen',
    icon: kyraIcons.summary,
    section: 'super_platform',
    module: 'superadmin',
  },
  {
    to: '/superadmin/organizaciones',
    label: 'Organizaciones',
    icon: kyraIcons.orgs,
    section: 'super_platform',
    module: 'superadmin',
  },
  {
    to: '/superadmin/usuarios',
    label: 'Usuarios plataforma',
    icon: kyraIcons.users,
    section: 'super_platform',
    module: 'superadmin',
  },
  {
    to: '/superadmin/planes',
    label: 'Planes comerciales',
    icon: kyraIcons.plans,
    section: 'super_commercial',
    module: 'superadmin',
  },
  {
    to: '/superadmin/reporte-comercial',
    label: 'Reporte comercial',
    icon: kyraIcons.trend,
    section: 'super_commercial',
    module: 'superadmin',
  },
  {
    to: '/tesoreria',
    label: 'Inicio',
    icon: kyraIcons.home,
    section: 'daily',
    module: 'tesoreria',
    requiredFeatures: [
      'treasury',
      'treasury_config',
      'treasury_reports',
      'reports',
      'budgets',
      'catalogs',
    ],
  },
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: kyraIcons.dashboard,
    section: 'analysis',
    module: 'tesoreria',
    requiredFeatures: ['treasury', 'treasury_reports', 'reports'],
  },
  {
    to: '/operaciones',
    label: 'Operaciones',
    icon: kyraIcons.ops,
    section: 'daily',
    module: 'tesoreria',
    requiredScope: 'operations:read',
    requiredFeatures: ['treasury'],
    requiredCapabilitiesAny: ['treasury.operations.read'],
  },
  {
    to: '/importar-operaciones',
    label: 'Importar operaciones',
    icon: kyraIcons.import,
    section: 'daily',
    module: 'tesoreria',
    requiredScope: 'operations:write',
    requiredFeatures: ['treasury'],
  },
  {
    to: '/cuentas',
    label: 'Cuentas',
    icon: kyraIcons.wallet,
    section: 'daily',
    module: 'tesoreria',
    requiredFeatures: ['treasury', 'treasury_config'],
  },
  {
    to: '/conciliaciones',
    label: 'Conciliaciones',
    icon: kyraIcons.conc,
    section: 'analysis',
    module: 'tesoreria',
    requiredScope: 'operations:read',
    requiredFeatures: ['treasury'],
  },
  {
    to: '/informe-financiero',
    label: 'Informe financiero',
    icon: kyraIcons.finreport,
    section: 'analysis',
    module: 'tesoreria',
    requiredFeatures: ['treasury_reports', 'reports', 'treasury'],
  },
  {
    to: '/reportes',
    label: 'Reportes',
    icon: kyraIcons.reports,
    section: 'analysis',
    module: 'tesoreria',
    requiredFeatures: ['treasury_reports', 'reports', 'treasury'],
  },
  {
    to: '/presupuestos',
    label: 'Presupuestos',
    icon: kyraIcons.budget,
    section: 'analysis',
    module: 'tesoreria',
    adminOnly: true,
    requiredFeatures: ['budgets'],
  },
  {
    to: '/membresias',
    label: 'Inicio',
    icon: kyraIcons.home,
    section: 'membership_ops',
    module: 'membresias',
    requiredScope: 'members:read',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/membresias/miembros',
    label: 'Miembros',
    icon: kyraIcons.member,
    section: 'membership_ops',
    module: 'membresias',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/membresias/recibos',
    label: 'Recibos de caja',
    icon: kyraIcons.receipt,
    section: 'membership_ops',
    module: 'membresias',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/membresias/cargos',
    label: 'Cargos eventuales',
    icon: kyraIcons.charge,
    section: 'membership_ops',
    module: 'membresias',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/membresias/reportes',
    label: 'Reportes',
    icon: kyraIcons.reports,
    section: 'membership_ops',
    module: 'membresias',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/membresias/cuotas/importar',
    label: 'Importar cuotas',
    icon: kyraIcons.feeimport,
    section: 'membership_ops',
    module: 'membresias',
    requiredScope: 'people:write',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/membresias/cuotas/carga-masiva',
    label: 'Carga masiva de cuotas',
    icon: kyraIcons.bulk,
    section: 'membership_ops',
    module: 'membresias',
    requiredScope: 'people:write',
    requiredFeatures: ['memberships'],
  },
  {
    to: '/configuracion',
    label: 'General',
    icon: kyraIcons.settings,
    section: 'config_general',
    module: 'configuracion',
    adminOnly: true,
  },
  {
    to: '/configuracion/empresa',
    label: 'Empresa',
    icon: kyraIcons.company,
    section: 'config_general',
    module: 'configuracion',
    adminOnly: true,
  },
  {
    to: '/configuracion/usuarios',
    label: 'Usuarios y acceso',
    icon: kyraIcons.access,
    section: 'config_general',
    module: 'configuracion',
    adminOnly: true,
    ownerOnly: true,
    requiredScope: 'members:read',
  },
  {
    to: '/configuracion/catalogos',
    label: 'Catálogos',
    icon: kyraIcons.catalogs,
    section: 'config_general',
    module: 'configuracion',
    adminOnly: true,
    requiredFeatures: ['catalogs', 'treasury_config', 'treasury', 'logistics', 'memberships'],
  },
  {
    to: '/configuracion/tasa-cambio',
    label: 'Tasa de cambio',
    icon: kyraIcons.fx,
    section: 'config_general',
    module: 'configuracion',
    adminOnly: true,
    requiredScope: 'logistics:read',
    requiredFeatures: ['logistics'],
  },
  {
    to: '/configuracion/formas-pago',
    label: 'Formas de pago',
    icon: kyraIcons.payment,
    section: 'config_general',
    module: 'configuracion',
    adminOnly: true,
    requiredScope: 'logistics:read',
    requiredFeatures: ['logistics'],
  },

  {
    to: '/logistica',
    label: 'Inicio',
    icon: kyraIcons.home,
    section: 'log_ops',
    module: 'logistica',
    requiredScope: 'logistics:read',
    requiredFeatures: ['logistics'],
  },
  {
    to: '/logistica/resumen',
    label: 'Resumen',
    icon: kyraIcons.dashboard,
    section: 'log_analysis',
    module: 'logistica',
    requiredScope: 'logistics:read',
    requiredFeatures: ['logistics', 'logistics_reports', 'reports'],
  },
  {
    to: '/logistica/operaciones',
    label: 'Operaciones',
    icon: kyraIcons.ops,
    section: 'log_ops',
    module: 'logistica',
    requiredScope: 'logistics:read',
    requiredFeatures: ['logistics'],
  },
  {
    to: '/logistica/items',
    label: 'Productos y servicios',
    icon: kyraIcons.cube,
    section: 'log_inventory',
    module: 'logistica',
    requiredScope: 'logistics:read',
    requiredFeatures: ['logistics'],
  },
  {
    to: '/logistica/existencias',
    label: 'Existencias',
    icon: kyraIcons.stock,
    section: 'log_inventory',
    module: 'logistica',
    requiredScope: 'inventory:read',
    requiredFeatures: ['logistics_reports', 'reports', 'inventory', 'logistics'],
  },
  {
    to: '/logistica/analisis-items',
    label: 'Análisis de inventario',
    icon: kyraIcons.analysis,
    section: 'log_analysis',
    module: 'logistica',
    requiredScope: 'inventory:read',
    requiredFeatures: ['inventory', 'logistics'],
  },
  {
    to: '/logistica/tomas-inventario',
    label: 'Tomas de inventario',
    icon: kyraIcons.stocktake,
    section: 'log_inventory',
    module: 'logistica',
    requiredScope: 'inventory:read',
    requiredFeatures: ['inventory', 'logistics'],
  },
  {
    to: '/logistica/almacenes',
    label: 'Almacenes',
    icon: kyraIcons.warehouse,
    section: 'log_admin',
    module: 'logistica',
    requiredScope: 'inventory:read',
    requiredFeatures: ['inventory', 'logistics'],
  },
];

const navSectionMeta: Record<NavSectionKey, { label: string; order: number }> = {
  daily: { label: 'Uso diario', order: 1 },
  analysis: { label: 'Control y análisis', order: 2 },
  membership_ops: { label: 'Operación', order: 1 },
  log_ops: { label: 'Operación', order: 1 },
  log_inventory: { label: 'Inventario', order: 2 },
  log_analysis: { label: 'Control y análisis', order: 3 },
  log_admin: { label: 'Administración', order: 4 },
  config_general: { label: 'Organización', order: 1 },
  public_site: { label: 'Contenido', order: 1 },
  super_platform: { label: 'Plataforma', order: 1 },
  super_commercial: { label: 'Comercial', order: 2 },
};

const isMobileWidth = () =>
  typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false;

export function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme, applyTheme } = useThemeStore();
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  const [navOpen, setNavOpen] = useState(() => !isMobileWidth());
  const [menuOpen, setMenuOpen] = useState(false);
  const [moduleMenuOpen, setModuleMenuOpen] = useState(false);
  const { user } = useAuthStore();
  const {
    organizations,
    activeOrganizationId,
    role,
    scopes,
    features,
    claims,
    license,
    publicSiteAccess,
    moduleAccess,
    usesLegacyAccessFallback,
    setActiveOrg,
    setTenant,
  } = useTenantStore();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const moduleSwitchRef = useRef<HTMLDivElement | null>(null);
  const lastModuleRef = useRef<ModuleKey>(readLastModule());

  const routeModule: ModuleKey = location.pathname.startsWith('/superadmin')
    ? 'superadmin'
    : location.pathname.startsWith('/public') || location.pathname.startsWith('/sitio-publico')
      ? 'sitio_publico'
      : location.pathname.startsWith('/configuracion')
        ? 'configuracion'
        : location.pathname.startsWith('/membresias') ||
            location.pathname === '/recibos' ||
            location.pathname === '/cargos'
          ? 'membresias'
          : location.pathname.startsWith('/logistica')
            ? 'logistica'
            : 'tesoreria';
  const isPeopleDirectory = location.pathname === '/personas';
  const navigationState = location.state as { fromModule?: unknown } | null;
  const peopleOriginModule = isModuleKey(navigationState?.fromModule)
    ? navigationState.fromModule
    : null;
  const currentModule = isPeopleDirectory
    ? (peopleOriginModule ?? lastModuleRef.current)
    : routeModule;
  const isSuperadmin = Boolean(user?.isSuperadmin || claims?.superadmin);
  const canAccessPeople =
    (isSuperadmin || scopes.includes('people:read')) &&
    (routeModule !== 'sitio_publico' || isPeopleDirectory);

  useEffect(() => {
    const moduleToRemember = isPeopleDirectory ? peopleOriginModule : routeModule;
    if (!moduleToRemember) return;
    lastModuleRef.current = moduleToRemember;
    persistLastModule(moduleToRemember);
  }, [isPeopleDirectory, peopleOriginModule, routeModule]);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    applyTheme(next);
  };

  const activeOrg = useMemo(() => {
    return organizations.find((org) => org.id === activeOrganizationId) ?? null;
  }, [organizations, activeOrganizationId]);

  const activeOrgName = activeOrg?.nombre ?? 'Sin organización';

  // Organización para la que se cargó el contexto (rol, scopes, features, licencia) vía /api/auth/me.
  const contextOrgIdRef = useRef<number | null>(activeOrganizationId);

  const refreshTenantContext = async (orgId: number, previousOrgId: number | null) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await api.get('/api/auth/me', {
        params: { _ts: Date.now() },
        headers: { Authorization: token ? `Bearer ${token}` : undefined, 'x-org-id': orgId },
      });
      const {
        organizations: orgs,
        activeOrganizationId: activeOrgId,
        role: activeRole,
        scopes: refreshedScopes,
        features: refreshedFeatures,
        license: refreshedLicense,
        publicSiteAccess: refreshedPublicSiteAccess,
        moduleAccess: refreshedModuleAccess,
        usesLegacyAccessFallback: refreshedUsesLegacyAccessFallback,
      } = res.data;
      setTenant(
        orgs,
        activeOrgId,
        activeRole,
        refreshedScopes ?? [],
        refreshedFeatures ?? [],
        refreshedLicense ?? null,
        refreshedPublicSiteAccess ?? null,
        refreshedModuleAccess ?? [],
        Boolean(refreshedUsesLegacyAccessFallback),
      );
      contextOrgIdRef.current = useTenantStore.getState().activeOrganizationId;
    } catch {
      if (previousOrgId) {
        contextOrgIdRef.current = previousOrgId;
        setActiveOrg(previousOrgId);
      }
    }
  };

  // Cualquier cambio de empresa (selector, enlaces QR de Operaciones/Recibos) recarga el contexto
  // de acceso de la nueva empresa; la caché de datos se descarta en main.tsx.
  useEffect(() => {
    const previousOrgId = contextOrgIdRef.current;
    if (activeOrganizationId === null || activeOrganizationId === previousOrgId) return;
    contextOrgIdRef.current = activeOrganizationId;
    void refreshTenantContext(activeOrganizationId, previousOrgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId]);

  const handleOrgSwitch = (orgId: number) => {
    if (orgId === activeOrganizationId) return;
    setActiveOrg(orgId);
  };

  const switchModule = (module: ModuleKey) => {
    setModuleMenuOpen(false);
    if (module === currentModule) return;
    if (!availableModules[module]) return;
    navigate(moduleMeta[module].defaultRoute);
  };

  const hasAnyFeature = (required?: string[]) => {
    if (isSuperadmin) return true;
    if (!required?.length) return true;
    const enabled = new Set(features);
    if (enabled.has('reports')) {
      enabled.add('treasury_reports');
      enabled.add('logistics_reports');
    }
    return required.some((feature) => enabled.has(feature));
  };

  const modularCapabilitySet = useMemo(
    () => new Set(moduleAccess.flatMap((assignment) => assignment.capabilities ?? [])),
    [moduleAccess],
  );

  const modularModuleSet = useMemo(
    () => new Set(moduleAccess.map((assignment) => assignment.moduleKey)),
    [moduleAccess],
  );

  const isUsingModularAccess = !usesLegacyAccessFallback && moduleAccess.length > 0;

  const hasAnyCapability = (required?: string[]) => {
    if (isSuperadmin) return true;
    if (!required?.length) return true;
    if (!isUsingModularAccess) return true;
    return required.some((capability) => modularCapabilitySet.has(capability));
  };

  const availableModules = useMemo<Record<ModuleKey, boolean>>(() => {
    if (isSuperadmin) {
      return {
        tesoreria: true,
        membresias: true,
        logistica: true,
        configuracion: true,
        sitio_publico: true,
        superadmin: true,
      };
    }
    if (isUsingModularAccess) {
      return {
        tesoreria:
          modularModuleSet.has('treasury') ||
          modularModuleSet.has('treasury_reports') ||
          modularModuleSet.has('budgets'),
        membresias: modularModuleSet.has('memberships'),
        logistica:
          modularModuleSet.has('logistics') ||
          modularModuleSet.has('inventory') ||
          modularModuleSet.has('logistics_reports'),
        configuracion: role === 'admin',
        sitio_publico: Boolean(publicSiteAccess?.can_read),
        superadmin: isSuperadmin,
      };
    }
    const enabled = new Set(features);
    if (enabled.has('reports')) {
      enabled.add('treasury_reports');
      enabled.add('logistics_reports');
    }
    return {
      tesoreria: [
        'treasury',
        'treasury_config',
        'treasury_reports',
        'reports',
        'budgets',
        'catalogs',
      ].some((feature) => enabled.has(feature)),
      membresias: enabled.has('memberships'),
      logistica:
        enabled.has('logistics') || enabled.has('inventory') || enabled.has('logistics_reports'),
      configuracion: role === 'admin',
      sitio_publico: Boolean(publicSiteAccess?.can_read),
      superadmin: isSuperadmin,
    };
  }, [
    features,
    isSuperadmin,
    isUsingModularAccess,
    modularModuleSet,
    publicSiteAccess?.can_read,
    role,
  ]);

  const availableModuleKeys = useMemo(
    () => (Object.keys(moduleMeta) as ModuleKey[]).filter((module) => availableModules[module]),
    [availableModules],
  );

  const firstAvailableModule = availableModuleKeys[0];
  const currentTourId = moduleTourIds[currentModule];
  const otherAvailableModules = availableModuleKeys.filter((module) => module !== currentModule);

  const canAccessNavItem = (item: NavItem) => {
    if (!availableModules[item.module]) return false;
    if (item.adminOnly && role !== 'admin' && !isSuperadmin) return false;
    if (item.ownerOnly && !activeOrg?.is_owner && !isSuperadmin) return false;
    if (item.publicSiteManageOnly && !isSuperadmin && !publicSiteAccess?.can_manage_editors)
      return false;
    if (item.requiredScope && !scopes.includes(item.requiredScope) && !isSuperadmin) return false;
    if (!hasAnyFeature(item.requiredFeatures)) return false;
    if (!hasAnyCapability(item.requiredCapabilitiesAny)) return false;
    return true;
  };

  const accessibleNavItems = navItems.filter(canAccessNavItem);
  const visibleNavItems = accessibleNavItems.filter((item) => item.module === currentModule);
  const quickNavigationItems: QuickNavigationItem[] = accessibleNavItems.map((item) => ({
    id: `navigation:${item.module}:${item.to}`,
    to: item.to,
    label: item.label,
    moduleLabel: moduleMeta[item.module].label,
    sectionLabel: navSectionMeta[item.section].label,
    icon: item.icon,
    kind: 'navigation',
    keywords: [item.label, moduleMeta[item.module].label, navSectionMeta[item.section].label],
  }));
  for (const action of quickActionDefinitions) {
    if (!availableModules[action.module]) continue;
    if (!hasAnyFeature(action.requiredFeatures)) continue;
    if (
      !isSuperadmin &&
      action.requiredScopesAny.length > 0 &&
      !action.requiredScopesAny.some((scope) => scopes.includes(scope))
    ) {
      continue;
    }
    quickNavigationItems.push({
      id: action.id,
      to: action.to,
      label: action.label,
      moduleLabel: moduleMeta[action.module].label,
      sectionLabel: 'Acción rápida',
      description: action.description,
      icon: action.icon,
      kind: 'action',
      keywords: action.keywords,
    });
  }
  if (canAccessPeople) {
    quickNavigationItems.push({
      id: 'navigation:people',
      to: '/personas',
      label: 'Personas',
      moduleLabel: 'Directorio',
      sectionLabel: 'Personas y contactos',
      icon: kyraIcons.users,
      kind: 'navigation',
      keywords: ['persona', 'personas', 'contacto', 'cliente', 'proveedor', 'miembro'],
      state: { fromModule: currentModule },
    });
  }

  const groupedNavItems = useMemo(() => {
    const groups = new Map<NavSectionKey, NavItem[]>();
    for (const item of visibleNavItems) {
      const current = groups.get(item.section) ?? [];
      current.push(item);
      groups.set(item.section, current);
    }
    return Array.from(groups.entries())
      .sort((a, b) => navSectionMeta[a[0]].order - navSectionMeta[b[0]].order)
      .map(([section, items]) => ({ section, items }));
  }, [visibleNavItems]);

  const currentSectionLabel = useMemo(() => {
    const routeToLabel: Record<string, string> = {
      '/tesoreria': 'Inicio',
      '/dashboard': 'Dashboard',
      '/onboarding': 'Configuración empresa',
      '/configuracion': 'General',
      '/configuracion/empresa': 'Configuración empresa',
      '/configuracion/usuarios': 'Usuarios y acceso',
      '/configuracion/catalogos': 'Catálogos',
      '/perfil': 'Perfil',
      '/public': 'Landing',
      '/superadmin': 'Superadmin',
      '/superadmin/usuarios': 'Usuarios plataforma',
      '/superadmin/organizaciones': 'Organizaciones',
      '/superadmin/planes': 'Planes comerciales',
      '/superadmin/reporte-comercial': 'Reporte comercial',
    };
    const matchedItem = [...visibleNavItems]
      .sort((a, b) => b.to.length - a.to.length)
      .find((item) => {
        const cleanTo = item.to.split('#')[0];
        return location.pathname === cleanTo || location.pathname.startsWith(`${cleanTo}/`);
      });
    if (matchedItem) return matchedItem.label;
    return routeToLabel[location.pathname] ?? moduleMeta[currentModule].label;
  }, [location.pathname, visibleNavItems, currentModule]);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (!moduleMenuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (moduleSwitchRef.current && target && !moduleSwitchRef.current.contains(target)) {
        setModuleMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setModuleMenuOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [moduleMenuOpen]);

  useEffect(() => {
    setModuleMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const syncViewport = () => {
      const mobile = isMobileWidth();
      setIsMobile(mobile);
      if (mobile) {
        setNavOpen(false);
      } else {
        setNavOpen(true);
      }
    };
    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    if (isMobile) setNavOpen(false);
  }, [location.pathname, location.hash, isMobile]);

  useEffect(() => {
    if (location.pathname === '/onboarding') return;
    if (currentModule === 'superadmin') return;
    if (availableModules[currentModule]) return;
    if (!firstAvailableModule) return;
    navigate(moduleMeta[firstAvailableModule].defaultRoute, { replace: true });
  }, [availableModules, currentModule, firstAvailableModule, location.pathname, navigate]);

  return (
    <div
      className={`app-shell ${navOpen ? 'nav-open' : 'nav-closed'}`}
      style={{ '--module-accent': moduleMeta[currentModule].accent } as CSSProperties}
    >
      <aside id="main-sidebar" className={`sidebar ${navOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo brand-logo">
            <BrandLogo
              variant={navOpen ? 'horizontal' : 'icon'}
              className={`brand-logo-image ${navOpen ? 'brand-logo-image--expanded' : 'brand-logo-image--collapsed'}`}
            />
          </div>
          <button
            type="button"
            onClick={() => setNavOpen(!navOpen)}
            className="ghost"
            aria-label={navOpen ? 'Contraer menú lateral' : 'Expandir menú lateral'}
            aria-expanded={navOpen}
            aria-controls="main-sidebar-navigation"
          >
            {navOpen ? '⟨' : '⟩'}
          </button>
        </div>

        {availableModuleKeys.length > 1 && (
          <div
            ref={moduleSwitchRef}
            data-tour="module-switcher"
            className={`module-switch ${moduleMenuOpen ? 'is-open' : ''}`}
            role="navigation"
            aria-label="Módulos"
            onMouseEnter={() => {
              if (!isMobile) setModuleMenuOpen(true);
            }}
            onMouseLeave={() => {
              if (!isMobile) setModuleMenuOpen(false);
            }}
          >
            <button
              type="button"
              className="module-switch-trigger"
              title={moduleMeta[currentModule].label}
              aria-label={`Módulo actual: ${moduleMeta[currentModule].label}. Cambiar módulo`}
              aria-haspopup="menu"
              aria-expanded={moduleMenuOpen}
              onClick={() => setModuleMenuOpen((open) => !open)}
            >
              <ModuleTile tone={currentModule} size={30} />
              {navOpen ? <span>{moduleMeta[currentModule].label}</span> : null}
              {navOpen ? (
                <CaretDown className="module-switch-caret" size={15} weight="bold" />
              ) : null}
            </button>
            <div className="module-switch-menu" role="menu" aria-hidden={!moduleMenuOpen}>
              {otherAvailableModules.map((module) => {
                return (
                  <button
                    key={module}
                    type="button"
                    className="module-switch-option"
                    role="menuitem"
                    style={{ '--option-accent': moduleMeta[module].accent } as CSSProperties}
                    onClick={() => switchModule(module)}
                  >
                    <ModuleTile tone={module} size={34} />
                    <span>{moduleMeta[module].label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <nav id="main-sidebar-navigation">
          {groupedNavItems.map(({ section, items }) => (
            <div key={section} className="nav-section">
              {navOpen && <p className="nav-section-title">{navSectionMeta[section].label}</p>}
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={
                      item.to === '/superadmin' ||
                      item.to === '/logistica' ||
                      item.to === '/membresias'
                    }
                    className={({ isActive }) => (isActive ? 'active' : '')}
                    title={item.label}
                    aria-label={item.label}
                    onClick={() => {
                      if (isMobile) setNavOpen(false);
                    }}
                  >
                    <span className="nav-icon">
                      <Icon size={18} weight="duotone" />
                    </span>
                    <span className="nav-label">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
          {canAccessPeople ? (
            <div className="nav-section nav-section--directory">
              {navOpen ? <p className="nav-section-title">Directorio</p> : null}
              <NavLink
                to="/personas"
                state={{ fromModule: currentModule }}
                className={({ isActive }) => (isActive ? 'active' : '')}
                title="Personas"
                aria-label="Personas"
                onClick={() => {
                  if (isMobile) setNavOpen(false);
                }}
              >
                <span className="nav-icon">
                  <KyraIcon name="users" size={18} />
                </span>
                <span className="nav-label">Personas</span>
              </NavLink>
            </div>
          ) : null}
        </nav>
        <div className="sidebar-footer">
          <button className="ghost" onClick={toggleTheme}>
            Tema: {theme === 'light' ? 'Claro' : 'Oscuro'}
          </button>
          <button
            className="primary"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
          >
            Logout
          </button>
        </div>
      </aside>
      <main>
        <header className="topbar">
          <button
            className="ghost mobile-nav-toggle"
            onClick={() => setNavOpen(!navOpen)}
            aria-label="Abrir menú"
            aria-expanded={navOpen}
            aria-controls="main-sidebar"
          >
            ☰
          </button>
          <div className="topbar-context">
            <span className="topbar-context-icon">
              <ModuleTile tone={currentModule} size={40} />
            </span>
            <span className="topbar-context-copy">
              <small>{moduleMeta[currentModule].label}</small>
              <strong>{isPeopleDirectory ? 'Personas' : currentSectionLabel}</strong>
            </span>
          </div>
          <QuickNavigationSearch
            items={quickNavigationItems}
            onNavigate={(item) => navigate(item.to, { state: item.state })}
          />
          <div className="actions">
            <div className="org-name" title={activeOrgName}>
              {activeOrg?.logo_url ? (
                <img src={activeOrg.logo_url} alt={activeOrgName} className="org-logo" />
              ) : (
                <BrandLogo variant="icon" className="org-logo org-logo--brand" />
              )}
              <span className="org-name-text">{activeOrgName}</span>
            </div>
            {role && <span className="badge">{role}</span>}
            <div className="user-menu" ref={menuRef}>
              {currentTourId && location.pathname === moduleMeta[currentModule].defaultRoute ? (
                <button
                  type="button"
                  className="ghost icon-btn"
                  data-tour="help-button"
                  onClick={() => useTourStore.getState().requestRestart(currentTourId)}
                  title="Ver guía de este módulo"
                  aria-label="Ver guía de este módulo"
                >
                  <Question size={18} weight="bold" />
                </button>
              ) : null}
              <button
                className="ghost icon-btn"
                onClick={toggleTheme}
                title="Cambiar tema"
                aria-label="Cambiar tema"
              >
                {theme === 'light' ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" role="img">
                    <path
                      d="M12 2.25a.75.75 0 0 1 .75.75V4.5a.75.75 0 0 1-1.5 0V3a.75.75 0 0 1 .75-.75ZM12 6.75a5.25 5.25 0 1 0 0 10.5a5.25 5.25 0 0 0 0-10.5Zm9 5.25a.75.75 0 0 1-.75.75H18.75a.75.75 0 0 1 0-1.5h1.5a.75.75 0 0 1 .75.75Zm-15 0a.75.75 0 0 1-.75.75H3.75a.75.75 0 0 1 0-1.5h1.5a.75.75 0 0 1 .75.75Zm12.02-6.02a.75.75 0 0 1 0 1.06l-1.06 1.06a.75.75 0 1 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0ZM7.04 16.96a.75.75 0 0 1 0 1.06l-1.06 1.06a.75.75 0 0 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0Zm0-9.92a.75.75 0 0 1-1.06 0L4.92 5.98a.75.75 0 1 1 1.06-1.06l1.06 1.06a.75.75 0 0 1 0 1.06Zm11.04 9.92a.75.75 0 0 1-1.06 0l-1.06-1.06a.75.75 0 1 1 1.06-1.06l1.06 1.06a.75.75 0 0 1 0 1.06ZM12 19.5a.75.75 0 0 1 .75.75V21a.75.75 0 0 1-1.5 0v-.75a.75.75 0 0 1 .75-.75Z"
                      fill="currentColor"
                    />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" role="img">
                    <path
                      d="M21.752 15.002A9.718 9.718 0 0 1 12.25 2.25a.75.75 0 0 0-.83-.743A10.5 10.5 0 1 0 22.493 16.58a.75.75 0 0 0-.742-.83Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
              <button
                className="user-trigger"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <PersonAvatar
                  src={user?.photoURL}
                  alt={user?.displayName ?? 'Usuario'}
                  className="user-trigger-avatar"
                  emptyClassName="user-trigger-avatar"
                  iconSize={24}
                />
              </button>
              {menuOpen && (
                <div className="user-dropdown" role="menu">
                  <div className="user-meta">
                    <strong>{user?.displayName ?? 'Usuario'}</strong>
                    <span>{user?.email ?? ''}</span>
                  </div>
                  <div className="menu-divider" />
                  {organizations.length > 0 && (
                    <div className="menu-section">
                      <span className="menu-title">Cambiar compañía</span>
                      {organizations.map((org) => (
                        <button
                          key={org.id}
                          className="ghost"
                          onClick={() => {
                            handleOrgSwitch(org.id);
                            setMenuOpen(false);
                          }}
                        >
                          {org.nombre} ({org.role})
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="menu-divider" />
                  <button
                    className="ghost"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/perfil');
                    }}
                  >
                    Editar perfil
                  </button>
                  {(role === 'admin' || isSuperadmin) && (
                    <button className="ghost" onClick={() => navigate('/configuracion')}>
                      Configuración
                    </button>
                  )}
                  <button
                    className="primary"
                    onClick={async () => {
                      await logout();
                      navigate('/login');
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <section className="content">
          {license && !license.operational && (
            <div className="error-box" style={{ marginBottom: 16 }}>
              La licencia de esta organización no está activa. Algunas operaciones pueden estar
              bloqueadas hasta regularizar el estado comercial.
            </div>
          )}
          {/* Remonta la vista al cambiar de empresa para descartar su estado local (selecciones, filtros, fetches). */}
          <Fragment key={activeOrganizationId ?? 'no-org'}>
            <Outlet />
          </Fragment>
        </section>
      </main>
    </div>
  );
}
